import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { Customer } from './customerAPI'
import { cartApi } from '../cart/cartAPI'
import apiService from '../../services/api'
import { buildEndpoint, isCustomerServiceUser } from '../../utils/apiUtils'

// Customer slice state (equivalent to Flutter's CustomerPopupController)
interface CustomerState {
  // Customer data
  hasCustomerData: boolean
  customerId: number | null
  customerName: string
  customerPhone: string
  
  // Modal state
  isModalOpen: boolean
  isLoading: boolean
  isSearching: boolean
  
  // Form state
  phoneController: string
  nameController: string
  phoneError: boolean
  showNameField: boolean
  
  // Search results
  searchResults: Customer[]
  
  // Error handling
  error: string | null
}

const initialCustomerState: CustomerState = {
  // Customer data
  hasCustomerData: false,
  customerId: null,
  customerName: '',
  customerPhone: '',
  
  // Modal state
  isModalOpen: false,
  isLoading: false,
  isSearching: false,
  
  // Form state
  phoneController: '',
  nameController: '',
  phoneError: false,
  showNameField: false,
  
  // Search results
  searchResults: [],
  
  // Error handling
  error: null,
}

// Async thunk to transfer cart from cashier to customer when customer is added.
// The cart on screen is the source of truth: any leftover cart the customer has on the
// server (from a reset/abandoned order) is discarded so it doesn't reappear.
// sourceCartId/hasItems must be captured before the customer is selected, because
// selecting a customer switches cart loads to that customer's id.
export const transferCartToCustomer = createAsyncThunk(
  'customer/transferCartToCustomer',
  async (
    params: { customerId: number; sourceCartId?: string | number | null; hasItems: boolean },
    { dispatch }
  ) => {
    if (isCustomerServiceUser()) {
      return
    }

    const { customerId, hasItems } = params
    const sourceCartId = params.sourceCartId?.toString() ?? ''

    try {
      const response = await apiService.axios.get(
        buildEndpoint(`/cashier/carts/user/${customerId}`, customerId)
      )
      const customerCart = response.data?.data || response.data
      const customerCartId = customerCart?.id?.toString() ?? ''

      if (customerCartId && customerCartId !== sourceCartId) {
        await apiService.axios.delete(buildEndpoint(`/cashier/carts/${customerCartId}`))
      }
    } catch (error) {
      // Customer may have no previous cart; continue with the transfer
    }

    if (hasItems && sourceCartId) {
      try {
        await dispatch(cartApi.endpoints.updateCartUser.initiate({
          cartId: Number(sourceCartId),
          userId: customerId,
        })).unwrap()
      } catch (error: any) {
        // Don't throw error - customer is still added
      }
    }
  }
)

const customerSlice = createSlice({
  name: 'customer',
  initialState: initialCustomerState,
  reducers: {
    // Initialize with phone and name (equivalent to Flutter's initWith)
    initWith: (state, action: PayloadAction<{ phone: string; name: string }>) => {
      state.phoneController = action.payload.phone
      state.nameController = action.payload.name
      state.phoneError = false
      state.showNameField = false
      state.searchResults = []
      state.error = null
    },

    // Set customer data (equivalent to Flutter's setCustomerData)
    setCustomerData: (state, action: PayloadAction<Customer>) => {
      state.hasCustomerData = true
      state.customerId = action.payload.id
      state.customerName = action.payload.name
      state.customerPhone = action.payload.phone
    },

    // Clear customer data (equivalent to Flutter's clearCustomer)
    clearCustomerData: (state) => {
      state.hasCustomerData = false
      state.customerId = null
      state.customerName = ''
      state.customerPhone = ''
    },

    // Reset order (equivalent to Flutter's resetOrder)
    resetOrder: (state) => {
      state.hasCustomerData = false
      state.customerId = null
      state.customerName = ''
      state.customerPhone = ''
    },

    // Modal controls
    openModal: (state) => {
      state.isModalOpen = true
      state.phoneController = ''
      state.nameController = ''
      state.phoneError = false
      state.showNameField = false
      state.searchResults = []
      state.error = null
    },

    closeModal: (state) => {
      state.isModalOpen = false
      state.phoneController = ''
      state.nameController = ''
      state.phoneError = false
      state.showNameField = false
      state.searchResults = []
      state.error = null
    },

    // Form controls
    updatePhoneController: (state, action: PayloadAction<string>) => {
      state.phoneController = action.payload
      state.phoneError = false
      
      // Show name field if phone is long enough (equivalent to Flutter's phone validation)
      state.showNameField = action.payload.length >= 11
    },

    updateNameController: (state, action: PayloadAction<string>) => {
      state.nameController = action.payload
    },

    setPhoneError: (state, action: PayloadAction<boolean>) => {
      state.phoneError = action.payload
    },

    setShowNameField: (state, action: PayloadAction<boolean>) => {
      state.showNameField = action.payload
    },

    // Search controls
    setSearching: (state, action: PayloadAction<boolean>) => {
      state.isSearching = action.payload
    },

    setSearchResults: (state, action: PayloadAction<Customer[]>) => {
      state.searchResults = action.payload
    },

    clearSearchResults: (state) => {
      state.searchResults = []
    },

    // Select customer from search results (equivalent to Flutter's selectCustomerFromResults)
    selectCustomerFromResults: (state, action: PayloadAction<Customer>) => {
      const customer = action.payload
      state.hasCustomerData = true
      state.customerId = customer.id
      state.customerName = customer.name || ''
      state.customerPhone = customer.phone || ''
      state.isModalOpen = false
      state.searchResults = []
    },

    // Loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },

    // Error handling
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },

    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(transferCartToCustomer.pending, (state) => {
        state.isLoading = true
      })
      .addCase(transferCartToCustomer.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(transferCartToCustomer.rejected, (state) => {
        state.isLoading = false
      })
  },
})

export const { 
  initWith,
  setCustomerData, 
  clearCustomerData,
  resetOrder,
  openModal,
  closeModal,
  updatePhoneController,
  updateNameController,
  setPhoneError,
  setShowNameField,
  setSearching,
  setSearchResults,
  clearSearchResults,
  selectCustomerFromResults,
  setLoading,
  setError,
  clearError,
} = customerSlice.actions

// Export the CustomerState type
export type { CustomerState }

export default customerSlice.reducer


